import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import {
  Activity,
  ArchiveRestore,
  Bell,
  ClipboardList,
  Database,
  Eye,
  FileText,
  Inbox,
  List,
  ListChecks,
  Settings as SettingsIcon,
  Sparkles,
  Users,
} from 'lucide-react';
import { RequireAuth } from '@/components/layout/RequireAuth';
import { useCanDeleteEngagements } from '@/lib/api/engagement-lifecycle';
import { cn } from '@/lib/utils/cn';

/**
 * Firm-settings layout (T-0096 / E10).
 *
 * Mirrors `design_handoff_caliber_v2/prototype/screens-firm.js → settingsShell()`:
 * a left sub-nav rail of the eight firm-scoped routes from README §4
 * with the active item highlighted. The right pane renders the outlet.
 * T-0141 adds the requirement library (amendment 01 Decision 31), T-0184
 * the discovery frameworks beside it, T-0206 the vendor knowledge base
 * (amendment 03). The Methodology library entry is gone (C2-03, ADR-022
 * decision 5); its address lands on Assessment defaults.
 */
export const Route = createFileRoute('/settings')({
  component: SettingsLayout,
});

interface SubNavItem {
  to: string;
  label: string;
  icon: typeof Users;
  /** T-0161: shown only when `GET /api/v1/me` says `can_delete_engagements` (firm admins). */
  requiresCanDeleteEngagements?: boolean;
}

const SUB_NAV: SubNavItem[] = [
  { to: '/settings', label: 'Overview', icon: SettingsIcon },
  { to: '/settings/users', label: 'Users & roles', icon: Users },
  { to: '/settings/viewers', label: 'External viewers', icon: Eye },
  { to: '/settings/assessment-defaults', label: 'Assessment defaults', icon: ClipboardList },
  { to: '/settings/requirement-library', label: 'Requirement library', icon: ListChecks },
  { to: '/settings/discovery-frameworks', label: 'Discovery frameworks', icon: List },
  { to: '/settings/vendor-knowledge', label: 'Vendor knowledge base', icon: Database },
  { to: '/settings/queue', label: 'Promote-back queue', icon: Inbox },
  { to: '/settings/notifications', label: 'Notification policies', icon: Bell },
  { to: '/settings/my-notifications', label: 'My notifications', icon: Bell },
  { to: '/settings/ai-review', label: 'AI review', icon: Sparkles },
  { to: '/settings/ai-costs', label: 'AI cost governance', icon: Activity },
  { to: '/settings/brand', label: 'Brand & reports', icon: FileText },
  {
    to: '/settings/deleted-engagements',
    label: 'Deleted engagements',
    icon: ArchiveRestore,
    requiresCanDeleteEngagements: true,
  },
];

function SettingsLayout() {
  const { pathname } = useLocation();
  const canDeleteEngagements = useCanDeleteEngagements();
  const items = SUB_NAV.filter(
    (item) => !item.requiresCanDeleteEngagements || canDeleteEngagements,
  );
  return (
    <RequireAuth>
      <div className="grid min-w-0 grid-cols-[220px_1fr] gap-7 px-6 py-6">
        <aside className="sticky top-[72px] flex h-fit flex-col gap-0.5" aria-label="Firm settings">
          <div className="px-2.5 py-2 text-meta font-semibold uppercase tracking-wider text-ink-500">
            Firm settings
          </div>
          {items.map((item) => {
            const active =
              item.to === '/settings'
                ? pathname === '/settings' || pathname === '/settings/'
                : pathname === item.to || pathname.startsWith(item.to + '/');
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex h-8 items-center gap-2.5 rounded-card px-2.5 text-body-sm font-medium text-ink-700 no-underline transition-colors',
                  'hover:bg-paper-100 hover:text-ink-900',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  active && 'border border-ink-100 bg-paper-0 text-accent-amber-700',
                )}
                aria-current={active ? 'page' : undefined}
              >
                <item.icon
                  className={cn(
                    'h-4 w-4 flex-none',
                    active ? 'text-accent-amber-600' : 'text-ink-500',
                  )}
                  aria-hidden="true"
                />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </aside>
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </RequireAuth>
  );
}
